import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, Edit3, ArrowRight, Gauge } from "lucide-react";
import { listProfiles, getLatest } from "@/lib/profiles/storage";

// RecentOpStatesLauncher — Home-screen surface for the 3 most recently
// touched Operational States (P1 v6).
//
// Goal of the brief: "Open App → Create Kill Sheet in the fewest possible
// actions." Each card collapses the path through Profile Hub → OpState list
// → OpState detail → Create button into a single primary action on Home.
//
// Card contents (per brief):
//   - OpState label
//   - Well name (resolved from the OpState's _importedFrom audit if
//     possible, else the OpState's shift label as a hint)
//   - MD / TVD summary
//   - Mud Weight (if present)
//   - Last updated timestamp
//
// This launcher manages Operational State only - it does not launch Kill
// Sheets. Kill Sheet creation lives on Home's own canonical "Kill Sheets"
// card (Home.jsx goToKillSheets -> /wells/:wellId/kill-sheets/new), which
// does not depend on this component or on CreateKillSheetFromOpStateModal.
//
// Actions:
//   Open / Edit Operational State -> /profiles/opstate/<id>
//
// Empty state: a single card encouraging first OpState creation.
//
// data-testid contract:
//   recent-opstates-launcher, recent-opstate-card-<id>,
//   recent-opstate-edit-<id>, recent-opstates-empty

function _resolveWellName(opstate) {
  const audit = opstate?.payload?._importedFrom || {};
  const wellRef = Object.values(audit).find((row) => row?.kind === "well");
  if (!wellRef) return null;
  try {
    const w = getLatest("well", wellRef.id);
    return w?.label || w?.payload?.label || null;
  } catch { return null; }
}

function _fmtAge(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "—";
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1)   return "just now";
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)   return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function RecentOpStatesLauncher() {
  const nav = useNavigate();
  // listProfiles returns rows newest-first by updatedAt — slice top 3.
  const rows = useMemo(() => listProfiles("opstate").slice(0, 3), []);

  if (rows.length === 0) {
    return (
      <section data-testid="recent-opstates-empty"
               className="card-rs p-4 flex items-center gap-3"
               style={{ borderColor: "rgba(245,180,0,0.4)", background: "rgba(245,180,0,0.04)" }}>
        <Gauge className="w-5 h-5 text-[var(--rs-amber)] shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-semibold text-[var(--rs-text-1)]">No Operational States yet</div>
          <div className="text-[10.5px] text-[var(--rs-text-3)] leading-snug">
            Capture a snapshot of your current shift to enable one-tap Kill Sheet creation.
          </div>
        </div>
        <button data-testid="recent-opstates-empty-cta" type="button"
                onClick={() => nav("/profiles/opstate/new")}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-medium whitespace-nowrap"
                style={{ background: "var(--rs-amber)", color: "#0A0B0E", minHeight: 36 }}>
          Create OpState <ArrowRight className="w-4 h-4" />
        </button>
      </section>
    );
  }

  return (
    <section data-testid="recent-opstates-launcher">
      <div className="flex items-baseline justify-between mb-3">
        <div className="label-up text-[var(--rs-text-3)] inline-flex items-center gap-2">
          <Clock className="w-3.5 h-3.5" /> Recent Operations
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {rows.map((row) => (
          <RecentCard key={row.id} row={row}
                      onEdit={() => nav(`/profiles/opstate/${row.id}`)} />
        ))}
      </div>
    </section>
  );
}

function RecentCard({ row, onEdit }) {
  const p = row.payload || {};
  const wellName = _resolveWellName(row);
  const md  = p.currentMd  ? `${p.currentMd} ${p.units === "SI" ? "m" : "ft"}` : null;
  const tvd = p.currentTvd ? `${p.currentTvd} ${p.units === "SI" ? "m" : "ft"}` : null;
  const mw  = p.currentMudDensity
    ? `${p.currentMudDensity} ${p.units === "SI" ? "kg/m³" : "ppg"}`
    : null;
  return (
    <div data-testid={`recent-opstate-card-${row.id}`}
         className="card-rs p-3 flex flex-col gap-2">
      <div className="min-w-0">
        <div className="text-[12.5px] font-semibold text-[var(--rs-text-1)] truncate">
          {row.label || "(unlabeled)"}
        </div>
        {wellName && (
          <div className="text-[10px] text-[var(--rs-text-3)] truncate" data-testid={`recent-opstate-well-${row.id}`}>
            {wellName}
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-1 text-center">
        <DataCell label="MD"  value={md}  />
        <DataCell label="TVD" value={tvd} />
        <DataCell label="MW"  value={mw}  />
      </div>
      <div className="text-[10px] text-[var(--rs-text-3)]" data-testid={`recent-opstate-age-${row.id}`}>
        Updated {_fmtAge(row.updatedAt)} · v{row.version}
      </div>
      <div className="flex items-stretch gap-1.5 mt-1">
        <button type="button" onClick={onEdit}
                data-testid={`recent-opstate-edit-${row.id}`}
                className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-[11.5px] font-medium"
                style={{ background: "transparent", color: "var(--rs-amber)", border: "1px solid var(--rs-amber)", minHeight: 34 }}>
          <Edit3 className="w-3.5 h-3.5" /> Open / Edit Operational State
        </button>
      </div>
    </div>
  );
}

function DataCell({ label, value }) {
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-wider text-[var(--rs-text-3)]">{label}</div>
      <div className="text-[11px] font-mono text-[var(--rs-text-1)]">{value || "—"}</div>
    </div>
  );
}
